import { debounce } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { NumberField } from 'sutra/components/NumberField/NumberField'

import {
  CardActionButton,
  CardRoot,
  FilterChip,
  GroupPrefix,
  RevealOnCardHover,
} from 'components/filter-card/filter-card.styles'
import { FilterCardAccentBar } from 'components/FilterCardAccentBar'
import { InheritedFilterBadge } from 'components/InheritedFilterBadge'
import { FilterPopover } from 'features/charts-v2/components/FilterPopover'
import { NumericOperatorPopover } from 'features/charts-v2/components/NumericOperatorPopover'
import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import {
  POPOVER_SHADOW,
  useFixedPopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement'
import { type NumericOperatorType } from 'features/charts-v2/types'
import { type FilterOption } from 'features/filters/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

export type SentimentMode = 'sentence' | 'record'

const SENTIMENT_MODE_OPTIONS: { value: SentimentMode; label: string }[] = [
  { value: 'sentence', label: 'Sentence' },
  { value: 'record', label: 'Record' },
]

const NUMERIC_OPERATOR_LABEL: Record<NumericOperatorType, string> = {
  'greater than': 'Greater than',
  'greater than or equal to': 'Greater than or equal to',
  'less than': 'Less than',
  'less than or equal to': 'Less than or equal to',
  'equal to': 'Equal to',
  'not equal to': 'Not equal to',
}

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

// Small portal-rendered popover for switching Sentence / Record mode.
// Anchored flush above the card — identical pattern to RatingTypePopover.
interface SentimentModePopoverProps {
  isOpen: boolean
  selectedMode: SentimentMode
  onSelect: (mode: SentimentMode) => void
  onClose: () => void
  cardRef?: React.RefObject<HTMLElement>
}

const SentimentModePopover: React.FC<SentimentModePopoverProps> = ({
  isOpen,
  selectedMode,
  onSelect,
  onClose,
  cardRef,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null)
  const { placement, isMeasured, style } = useFixedPopoverPlacement({
    anchorRef: cardRef,
    popoverRef,
    isOpen,
  })

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (
        isOpen &&
        popoverRef.current &&
        !popoverRef.current.contains(target) &&
        cardRef?.current &&
        !cardRef.current.contains(target)
      ) {
        onClose()
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose, cardRef])

  // Auto-focus on open for standard focus containment while the popover is
  // open (arrow-key nav isn't needed here — Escape is handled below).
  useEffect(() => {
    if (isOpen) popoverRef.current?.focus()
  }, [isOpen])

  useEscapeDismiss({ isOpen, onClose })

  if (!isOpen) return null

  // document.body, not the ambient #popover-portal-container id — see
  // FilterSelectionPopover.tsx's popoverPortalTarget doc comment for why a
  // position: fixed overlay needs the root stacking context.
  return createPortal(
    <div
      ref={popoverRef}
      data-yogi-overlay="true"
      tabIndex={0}
      style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: POPOVER_SHADOW[placement],
        zIndex: 2000,
        padding: '8px',
        visibility: isMeasured ? 'visible' : 'hidden',
        outline: 'none',
        ...style,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {SENTIMENT_MODE_OPTIONS.map((opt) => (
        <RadioListOption
          key={opt.value}
          label={opt.label}
          isSelected={selectedMode === opt.value}
          onClick={() => {
            onSelect(opt.value)
            onClose()
          }}
        />
      ))}
    </div>,
    document.body,
  )
}

interface SentimentFilterCardProps {
  // The FilterOption for 'sentiment' — provides the value list (Positive / Negative / etc.)
  sentimentFilterOption: FilterOption | null
  // Which field currently holds categorical selections ('sentence' = sentiment, 'record' = post_sentiment)
  categoricalMode: SentimentMode
  // Currently selected categorical values (Positive / Negative / etc.)
  categoricalValues: string[]
  // Current avg-sentiment numeric filter state; null = no numeric filter
  numericOperator: NumericOperatorType | null
  numericValue: number | undefined
  onModeChange: (newMode: SentimentMode) => void
  onCategoricalChange: (values: string[]) => void
  onNumericChange: (
    operator: NumericOperatorType,
    value: number | undefined,
  ) => void
  onDelete: () => void
  // Presence renders this card read-only — inherited from a parent scope
  // rather than owned by the tile. See `FilterCard.tsx`'s (charts-v2) doc
  // comment on its equivalent prop for the full rationale. `sourceLabel` is
  // still supplied by every caller (see that file's equivalent prop) but no
  // longer read here — `InheritedFilterBadge` stopped needing it.
  inherited?: { supersededNote: string | null; sourceLabel: string }
  // Ownership accent bar — see `FilterCard.tsx`'s (charts-v2) equivalent prop.
  accent?: 'local' | 'global'
  // Forwarded to the accent bar and (when `inherited`) the lock badge — see
  // `FilterCard.tsx`'s (charts-v2) equivalent props.
  tileKind?: 'chart' | 'widget'
  onEditInheritedSource?: () => void
  // The picker group this card sits under ("Language"), shown as a muted
  // `Group > ` before the title. Omitted when hierarchy is off.
  groupLabel?: string
}

export const SentimentFilterCard: React.FC<SentimentFilterCardProps> = ({
  sentimentFilterOption,
  categoricalMode,
  categoricalValues,
  numericOperator,
  numericValue,
  onModeChange,
  onCategoricalChange,
  onNumericChange,
  onDelete,
  inherited,
  accent,
  tileKind,
  onEditInheritedSource,
  groupLabel,
}) => {
  const isReadOnly = inherited != null
  const [showModePopover, setShowModePopover] = useState(false)
  const [showValuesPopover, setShowValuesPopover] = useState(false)
  const [isOperatorDropdownOpen, setIsOperatorDropdownOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const operatorRef = useRef<HTMLSpanElement>(null)

  // Mirror of numericValue for immediate UI feedback (debounced store update)
  const [localNumericValue, setLocalNumericValue] = useState<
    number | undefined
  >(numericValue)

  // Keep local mirror in sync when the value changes externally
  useEffect(() => {
    setLocalNumericValue(numericValue)
  }, [numericValue])

  // Store the latest onNumericChange in a ref to avoid stale closures
  const onNumericChangeRef = useRef(onNumericChange)
  useEffect(() => {
    onNumericChangeRef.current = onNumericChange
  }, [onNumericChange])

  const currentOperator: NumericOperatorType = numericOperator ?? 'greater than'

  // Debounce value changes — same pattern as NumericFilterCard
  const debouncedNumericChange = useRef(
    debounce((op: NumericOperatorType, val: number | undefined) => {
      onNumericChangeRef.current(op, val)
    }, 500),
  ).current

  useEffect(() => {
    return () => debouncedNumericChange.cancel()
  }, [debouncedNumericChange])

  const handleNumericValueChange = useCallback(
    (newValue: number | null) => {
      if (newValue === null) {
        setLocalNumericValue(undefined)
        debouncedNumericChange(currentOperator, undefined)
        return
      }
      // Sentiment avg is bounded [0, 1].
      const clamped = Math.min(1, Math.max(0, newValue))
      setLocalNumericValue(clamped)
      debouncedNumericChange(currentOperator, clamped)
    },
    [debouncedNumericChange, currentOperator],
  )

  const handleOperatorChange = useCallback(
    (op: NumericOperatorType): void => {
      // Operator changes don't need debouncing; cancel any pending value debounce
      // so we don't race — send operator + current local value together.
      debouncedNumericChange.cancel()
      onNumericChange(op, localNumericValue)
    },
    [debouncedNumericChange, onNumericChange, localNumericValue],
  )

  // Truncated display of categorical values — mirrors FilterCard.getFieldValue
  const getValuesText = (): string => {
    if (categoricalValues.length === 0) return ''
    if (categoricalValues.length === 1) return categoricalValues[0]!
    if (categoricalValues.length === 2) return categoricalValues.join(', ')
    return `${categoricalValues.slice(0, 2).join(', ')}, or ${categoricalValues.length - 2} more`
  }

  const modeLabel = categoricalMode === 'record' ? 'Record' : 'Sentence'
  const hasNumeric = numericOperator !== null

  return (
    <>
      <CardRoot ref={cardRef} $disabled={false} $accent={accent}>
        {accent && (
          <FilterCardAccentBar
            accent={accent}
            tileKind={tileKind}
            onEditSource={onEditInheritedSource}
          />
        )}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
          <div style={{ width: '100%' }}>
            {/* Title row */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <TitleRow>
                <span
                  style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '14px',
                  }}
                >
                  {groupLabel && (
                    <GroupPrefix>
                      {groupLabel} {'> '}
                    </GroupPrefix>
                  )}
                  Sentiment
                </span>
                {inherited && (
                  <RevealOnCardHover>
                    <InheritedFilterBadge
                      onEditSource={onEditInheritedSource}
                    />
                  </RevealOnCardHover>
                )}
              </TitleRow>
              {!isReadOnly && (
                <RevealOnCardHover>
                  <CardActionButton
                    type="button"
                    $variant="danger"
                    aria-label="Remove filter"
                    title="Remove filter"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDelete()
                    }}
                  >
                    ×
                  </CardActionButton>
                </RevealOnCardHover>
              )}
            </div>

            {/* Detail rows */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                width: '100%',
              }}
            >
              {/* Row 1: for [mode] — always visible */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  position: 'relative',
                }}
              >
                <span
                  style={{
                    color: '#6b7280',
                    padding: '4px 8px',
                    fontSize: '12px',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  }}
                >
                  of
                </span>
                <FilterChip
                  $open={showModePopover}
                  $disabled={isReadOnly}
                  onClick={
                    isReadOnly
                      ? undefined
                      : () => {
                          if (!showModePopover) {
                            setShowValuesPopover(false)
                            setIsOperatorDropdownOpen(false)
                          }
                          setShowModePopover(!showModePopover)
                        }
                  }
                >
                  {modeLabel}
                </FilterChip>
              </div>

              {/* Row 2: is [values] — only when categorical values exist */}
              {categoricalValues.length > 0 && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                >
                  <span
                    style={{
                      color: '#6b7280',
                      padding: '4px 8px',
                      fontSize: '12px',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                    }}
                  >
                    is
                  </span>
                  <FilterChip
                    $open={showValuesPopover}
                    $disabled={isReadOnly}
                    style={{ width: '100%' }}
                    onClick={
                      isReadOnly
                        ? undefined
                        : () => {
                            if (!showValuesPopover) {
                              setShowModePopover(false)
                              setIsOperatorDropdownOpen(false)
                            }
                            setShowValuesPopover(!showValuesPopover)
                          }
                    }
                  >
                    {getValuesText()}
                  </FilterChip>
                </div>
              )}

              {/* Row 3: [operator] [value] — only when numeric filter exists */}
              {hasNumeric && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    gap: '8px',
                  }}
                >
                  <FilterChip
                    ref={operatorRef}
                    $open={isOperatorDropdownOpen}
                    $disabled={isReadOnly}
                    $color="#6b7280"
                    onClick={
                      isReadOnly
                        ? undefined
                        : () => {
                            if (!isOperatorDropdownOpen) {
                              setShowModePopover(false)
                              setShowValuesPopover(false)
                            }
                            setIsOperatorDropdownOpen(!isOperatorDropdownOpen)
                          }
                    }
                  >
                    {NUMERIC_OPERATOR_LABEL[currentOperator]}
                  </FilterChip>
                  <NumberField
                    value={localNumericValue ?? null}
                    onValueChange={handleNumericValueChange}
                    min={0}
                    max={1}
                    step={0.1}
                    style={{ width: '90px', fontSize: '12px', height: '30px' }}
                    placeholder="0–1"
                    disabled={isReadOnly}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </CardRoot>

      {/* Mode selector popover */}
      <SentimentModePopover
        isOpen={showModePopover && !isReadOnly}
        selectedMode={categoricalMode}
        onSelect={(newMode) => {
          setShowModePopover(false)
          onModeChange(newMode)
        }}
        onClose={() => setShowModePopover(false)}
        cardRef={cardRef}
      />

      {/* Categorical values popover — always renders from the 'sentiment' filter option */}
      {sentimentFilterOption && (
        <FilterPopover
          filter={sentimentFilterOption}
          isOpen={showValuesPopover && !isReadOnly}
          onClose={() => setShowValuesPopover(false)}
          onSelectionChange={onCategoricalChange}
          currentSelectedValues={categoricalValues}
          cardRef={cardRef}
        />
      )}

      {/* Numeric operator popover */}
      {isOperatorDropdownOpen && !isReadOnly && (
        <NumericOperatorPopover
          onOperatorSelect={handleOperatorChange}
          onClose={() => setIsOperatorDropdownOpen(false)}
          selectedOperator={currentOperator}
          triggerRef={operatorRef}
          cardRef={cardRef}
        />
      )}
    </>
  )
}
